import React, { useRef } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Link, useLocation } from "react-router-dom";

import { useThrottle } from "app/_helpers/hooks";
import { getQueryStringAsObject } from "app/_helpers/helper";
import {
  mineDetailsSectionView,
  personalDetailsFieldsView,
  personalDetailsSection,
  ViewField,
  ViewFieldContainer,
} from "./FormA.data";

export default function DownloadFormA({ fieldsObj, data }) {
  const ref = useRef();
  const location = useLocation();

  const handlePrint = useThrottle(
    async () => {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const canvas = await html2canvas(ref.current, {
        useCORS: true,
        backgroundColor: "white",
        scale: 2,
      });

      const imgData = canvas.toDataURL("image/jpeg");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Image size in pixels
      const imgPixelWidth = canvas.width;
      const imgPixelHeight = canvas.height;

      // Convert pixel size to mm (assuming 96 DPI)
      const pxToMm = (px) => (px * 25.4) / 96;

      const imgWidthMm = pxToMm(imgPixelWidth);
      const imgHeightMm = pxToMm(imgPixelHeight);

      // Scale down to fit within PDF dimensions
      const widthScale = pageWidth / imgWidthMm;
      const heightScale = pageHeight / imgHeightMm;
      const scale = Math.min(widthScale, heightScale);

      const scaledWidth = imgWidthMm * scale;
      const scaledHeight = imgHeightMm * scale;

      // Center the image
      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(imgData, "JPEG", x, y, scaledWidth, scaledHeight);
      await pdf.save(`${new Date().toISOString()}.pdf`);
    },
    [],
    2000
  );

  return (
    <>
      <div className="p-2" ref={ref}>
        <ViewSection
          {...personalDetailsSection}
          fields={personalDetailsFieldsView}
          data={data}
        />
        {data.staffType === "contractor" && (
          <div className="row mx-0">
            <div className="col-lg-10 col-md-10 col-12 px-0">
              <ViewSection {...mineDetailsSectionView} data={data} />
            </div>
          </div>
        )}
        {Object.keys(fieldsObj).map((key) =>
          fieldsObj[key].hideSectionInView ? null : (
            <ViewSection {...fieldsObj[key]} key={key} data={data} />
          )
        )}
      </div>
      <div className="p-2 text-center">
        <Link
          className="btn btn-secondary m-2"
          to={
            location.state?.backUrl ||
            getQueryStringAsObject().backUrl ||
            "/user-management/employee"
          }
        >
          Back
        </Link>
        <button className="btn btn-primary m-2" type="button" onClick={handlePrint}>
          Print
        </button>
      </div>
    </>
  );
}

function ViewSection({ hideTitleInView, noMarginTop, title, fields, data }) {
  return (
    <div className="m-0">
      {!hideTitleInView && title && (
        <SectionTitle title={title} noMarginTop={noMarginTop} />
      )}
      <div className="row px-3">
        {fields.map((field) => {
          if (field.staffType && field.staffType !== data.staffType) return null;

          const {
            inputType,
            customView,
            colView = inputType === "section" ? 12 : 6,
            colLgView = inputType === "section" ? 12 : 4,
            colMdView = inputType === "section" ? 12 : 4,
            colSmView = inputType === "section" ? 12 : 6,
          } = field;

          return customView ? (
            customView(data)
          ) : inputType === "section" ? (
            <>
              {field.title && !field.hideTitleInView && (
                <SectionTitle
                  title={field.title}
                  noMarginTop={field.noMarginTop}
                  noStyle
                />
              )}
              <ViewFieldContainer
                colLgView={colLgView}
                colMdView={colMdView}
                colSmView={colSmView}
                colView={colView}
                noBorder={!field.title || hideTitleInView}
              >
                <div className="row">
                  {field.fields.map((f) =>
                    f.staffType && f.staffType !== data.staffType ? null : (
                      <ViewField {...f} key={f.name} data={data} />
                    )
                  )}
                </div>
              </ViewFieldContainer>
            </>
          ) : (
            <ViewField {...field} key={field.name} data={data} />
          );
        })}
      </div>
    </div>
  );
}

function SectionTitle({ title, noStyle = false, noMarginTop = false }) {
  return (
    <div
      style={noStyle ? undefined : { paddingLeft: 1, paddingRight: 1 }}
      className={`w-100${noMarginTop ? "" : " mt-3"}`}
    >
      <div className="border p-2 w-100">
        <h4>{title}</h4>
      </div>
    </div>
  );
}
